import React, { useState, useEffect, useRef } from 'react';
import {
  UploadCloud, Copy, Check, Image as ImageIcon, Video, Trash2,
  Loader2, Play, ExternalLink, Sparkles, Filter, Plus, X, AlertCircle,
  Film, Settings, CheckCircle2, ShieldCheck, Key
} from 'lucide-react';
import { apiFetch } from '../lib/auth';
import { optimizeImageForUpload } from '../lib/imageUtils';
import type { MediaAsset } from '@zarin/types';

interface CloudinaryConfig {
  cloudName: string;
  apiKey: string;
  uploadPreset: string;
  isConfigured: boolean;
  hasSecret: boolean;
}

export default function MediaPage() {
  const [assets, setAssets]         = useState<MediaAsset[]>([]);
  const [loading, setLoading]       = useState(true);
  const [copiedId, setCopiedId]     = useState<string | null>(null);
  const [filterType, setFilterType] = useState<'all' | 'image' | 'video'>('all');
  const [cldConfig, setCldConfig]   = useState<CloudinaryConfig | null>(null);

  // Cloudinary Config form state
  const [cfgCloudName, setCfgCloudName]     = useState('');
  const [cfgApiKey, setCfgApiKey]           = useState('');
  const [cfgApiSecret, setCfgApiSecret]     = useState('');
  const [cfgUploadPreset, setCfgUploadPreset] = useState('');
  const [savingConfig, setSavingConfig]     = useState(false);
  const [configSuccess, setConfigSuccess]   = useState(false);

  // Uploading state
  const [uploading, setUploading]       = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string | null>(null);
  const [uploadError, setUploadError]   = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Preview Modal
  const [previewAsset, setPreviewAsset] = useState<MediaAsset | null>(null);

  // Add by URL Modal
  const [showUrlModal, setShowUrlModal] = useState(false);
  const [inputUrl, setInputUrl]         = useState('');
  const [inputAlt, setInputAlt]         = useState('');
  const [savingUrl, setSavingUrl]       = useState(false);

  // Setup Guide & Config Modal
  const [showSetupModal, setShowSetupModal] = useState(false);

  // Load Media Assets & Cloudinary Config
  const loadMedia = async () => {
    try {
      setLoading(true);
      const [mediaRes, configRes] = await Promise.all([
        apiFetch<{ data: MediaAsset[] }>('/media'),
        apiFetch<{ data: CloudinaryConfig }>('/media/cloudinary/config').catch(() => ({ data: null }))
      ]);
      setAssets(mediaRes.data || []);
      if (configRes?.data) {
        setCldConfig(configRes.data);
        setCfgCloudName(configRes.data.cloudName || '');
        setCfgApiKey(configRes.data.apiKey || '');
        setCfgUploadPreset(configRes.data.uploadPreset || '');
      }
    } catch (err) {
      console.error('Failed to load media:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCloudinaryConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingConfig(true);
    try {
      await apiFetch('/media/cloudinary/config', {
        method: 'POST',
        body: JSON.stringify({
          cloudName: cfgCloudName.trim(),
          apiKey: cfgApiKey.trim(),
          apiSecret: cfgApiSecret.trim(),
          uploadPreset: cfgUploadPreset.trim(),
        }),
      });
      setConfigSuccess(true);
      setTimeout(() => setConfigSuccess(false), 3000);
      loadMedia();
    } catch (err: any) {
      alert('Failed to save Cloudinary configuration: ' + (err?.detail || err?.title || err?.message || String(err)));
    } finally {
      setSavingConfig(false);
    }
  };

  useEffect(() => {
    loadMedia();
  }, []);

  // Copy URL with visual feedback
  const copyUrl = (id: string, url: string) => {
    navigator.clipboard.writeText(url);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Upload Files to Cloudinary / Cloudflare D1 Native Media
  const handleFilesSelected = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setUploadError(null);

    const total = files.length;
    let completed = 0;

    for (let i = 0; i < total; i++) {
      const file = files[i]!;
      setUploadProgress(`Uploading ${file.name} (${i + 1}/${total})…`);

      // Optimize image before upload to prevent latency and size issues
      const optimizedFile = file.type.startsWith('image/')
        ? await optimizeImageForUpload(file).catch(() => file)
        : file;

      const formData = new FormData();
      formData.append('file', optimizedFile);
      formData.append('folder', file.type.startsWith('video/') ? 'zarin-wear/videos' : 'zarin-wear/images');

      try {
        await apiFetch('/media/cloudinary/upload', {
          method: 'POST',
          body: formData,
        });
        completed++;
      } catch (err: any) {
        console.error('Upload failed for:', file.name, err);
        setUploadError(err?.detail || err?.title || `Failed to upload ${file.name}.`);
      }
    }

    setUploading(false);
    setUploadProgress(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    loadMedia();
  };

  // Drag and Drop handlers
  const [isDragging, setIsDragging] = useState(false);
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFilesSelected(e.dataTransfer.files);
  };

  // Delete Media
  const handleDelete = async (asset: MediaAsset) => {
    if (!confirm(`Are you sure you want to delete this ${asset.resourceType || 'asset'}?`)) return;
    try {
      await apiFetch(`/media/${asset.id}`, { method: 'DELETE' });
      setAssets(prev => prev.filter(a => a.id !== asset.id));
      if (previewAsset?.id === asset.id) setPreviewAsset(null);
    } catch (err: any) {
      alert(err?.title || 'Failed to delete asset');
    }
  };

  // Add Asset via URL
  const handleAddByUrl = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputUrl.trim()) return;

    setSavingUrl(true);
    try {
      const isVideo = inputUrl.match(/\.(mp4|webm|mov|avi)(\?.*)?$/i) !== null || inputUrl.includes('/video/upload/');
      const key = `cld_${crypto.randomUUID().slice(0, 8)}`;
      await apiFetch('/media', {
        method: 'POST',
        body: JSON.stringify({
          key,
          url: inputUrl.trim(),
          altText: inputAlt.trim() || null,
          mimeType: isVideo ? 'video/mp4' : 'image/jpeg',
          sizeBytes: 100000,
          resourceType: isVideo ? 'video' : 'image',
          thumbnailUrl: isVideo ? inputUrl.replace(/\.[a-zA-Z0-9]+$/, '.jpg') : inputUrl,
        }),
      });
      setInputUrl('');
      setInputAlt('');
      setShowUrlModal(false);
      loadMedia();
    } catch (err: any) {
      alert(err?.title || 'Failed to register media URL');
    } finally {
      setSavingUrl(false);
    }
  };

  // Filtered Assets
  const filteredAssets = assets.filter(a => {
    if (filterType === 'all') return true;
    const isVid = a.resourceType === 'video' || a.mimeType?.startsWith('video/');
    return filterType === 'video' ? isVid : !isVid;
  });

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'Unknown size';
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <h1 style={styles.title}>Media & Video Library</h1>
            <span style={styles.cldBadge}>
              <Sparkles size={12} />
              <span>Cloudinary CDN</span>
            </span>
          </div>
          <p style={styles.subtitle}>
            High-performance asset pipeline for editorial photography and 4K garment movement videos.
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button style={styles.btnSecondary} onClick={() => setShowSetupModal(true)}>
            <Settings size={15} />
            <span>Cloudinary Credentials</span>
          </button>
          <button style={styles.btnSecondary} onClick={() => setShowUrlModal(true)}>
            <Plus size={15} />
            <span>Add by URL</span>
          </button>
          <button style={styles.btnPrimary} onClick={() => fileInputRef.current?.click()} disabled={uploading}>
            {uploading ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <UploadCloud size={16} />}
            <span>Upload Media</span>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            style={{ display: 'none' }}
            onChange={e => handleFilesSelected(e.target.files)}
          />
        </div>
      </div>

      {/* Cloudinary Status Bar */}
      <div style={styles.statusBar}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <CheckCircle2 size={16} color="#4ADE80" />
          <span style={{ fontSize: '0.8125rem', color: '#FAF7F2' }}>
            Cloudinary Cloud: <strong style={{ color: '#C9A84C' }}>{cldConfig?.cloudName || 'zarinwear'}</strong>
          </span>
          <span style={{ color: '#666' }}>•</span>
          <span style={{ fontSize: '0.75rem', color: '#888' }}>
            Supports WebP/AVIF auto-transcoding, progressive video streaming, and adaptive bitrate.
          </span>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={() => setFilterType('all')}
            style={{ ...styles.filterTab, ...(filterType === 'all' ? styles.filterTabActive : {}) }}
          >
            All ({assets.length})
          </button>
          <button
            onClick={() => setFilterType('image')}
            style={{ ...styles.filterTab, ...(filterType === 'image' ? styles.filterTabActive : {}) }}
          >
            <ImageIcon size={13} />
            <span>Images ({assets.filter(a => !(a.resourceType === 'video' || a.mimeType?.startsWith('video/'))).length})</span>
          </button>
          <button
            onClick={() => setFilterType('video')}
            style={{ ...styles.filterTab, ...(filterType === 'video' ? styles.filterTabActive : {}) }}
          >
            <Film size={13} />
            <span>Videos ({assets.filter(a => a.resourceType === 'video' || a.mimeType?.startsWith('video/')).length})</span>
          </button>
        </div>
      </div>

      {/* Upload Error Banner */}
      {uploadError && (
        <div style={styles.errorBanner}>
          <AlertCircle size={16} />
          <span>{uploadError}</span>
          <button onClick={() => setUploadError(null)} style={{ background: 'transparent', border: 'none', color: '#FAF7F2', marginLeft: 'auto', cursor: 'pointer' }}>
            <X size={14} />
          </button>
        </div>
      )}

      {/* Drag & Drop Upload Zone */}
      <div
        style={{
          ...styles.uploadZone,
          borderColor: isDragging ? '#C9A84C' : '#333',
          background: isDragging ? 'rgba(201, 168, 76, 0.05)' : '#181818',
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {uploading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
            <Loader2 size={36} color="#C9A84C" style={{ animation: 'spin 1s linear infinite' }} />
            <h3 style={styles.uploadTitle}>{uploadProgress || 'Processing upload...'}</h3>
            <p style={styles.uploadDesc}>Uploading directly to Cloudinary and registering asset in D1.</p>
          </div>
        ) : (
          <>
            <div style={styles.uploadIconWrap}>
              <UploadCloud size={28} color="#C9A84C" />
            </div>
            <h3 style={styles.uploadTitle}>Drag and drop editorial photography & garment videos</h3>
            <p style={styles.uploadDesc}>
              Supports JPG, PNG, WebP, AVIF, GIF and MP4, WebM, MOV. Images and videos are transcoded and CDN-accelerated via Cloudinary.
            </p>
            <button style={styles.btnSecondary} onClick={() => fileInputRef.current?.click()}>
              Select Files from Device
            </button>
          </>
        )}
      </div>

      {/* Asset Grid */}
      {loading ? (
        <div style={styles.loadingBox}>
          <Loader2 size={28} color="#C9A84C" style={{ animation: 'spin 1s linear infinite' }} />
          <span style={{ color: '#888', fontSize: '0.875rem' }}>Fetching media assets from Cloudinary...</span>
        </div>
      ) : filteredAssets.length === 0 ? (
        <div style={styles.emptyCard}>
          <ImageIcon size={40} color="#444" style={{ marginBottom: 12 }} />
          <h3 style={styles.emptyTitle}>No media assets found</h3>
          <p style={styles.emptyDesc}>
            {filterType === 'video'
              ? 'No garment motion videos uploaded yet. Upload MP4/MOV videos to demonstrate fabric movement.'
              : 'Upload campaign photography or product lookbook assets to populate the library.'}
          </p>
          <button style={styles.btnPrimary} onClick={() => fileInputRef.current?.click()}>
            <UploadCloud size={15} />
            <span>Upload First File</span>
          </button>
        </div>
      ) : (
        <div style={styles.grid}>
          {filteredAssets.map((asset) => {
            const isVid = asset.resourceType === 'video' || asset.mimeType?.startsWith('video/');
            return (
              <div key={asset.id} style={styles.assetCard}>
                <div style={styles.assetMediaWrap} onClick={() => setPreviewAsset(asset)}>
                  {isVid ? (
                    <div style={styles.videoWrap}>
                      <video
                        src={asset.url}
                        poster={asset.thumbnailUrl || undefined}
                        style={styles.assetVideo}
                        muted
                        preload="metadata"
                      />
                      <div style={styles.playOverlay}>
                        <Play size={20} fill="#FAF7F2" color="#FAF7F2" />
                      </div>
                      <span style={styles.videoBadge}>VIDEO</span>
                    </div>
                  ) : (
                    <img
                      src={asset.url}
                      alt={asset.altText || 'Media Asset'}
                      style={styles.assetImg}
                      loading="lazy"
                    />
                  )}

                  <span style={styles.formatBadge}>
                    {asset.format ? asset.format.toUpperCase() : isVid ? 'MP4' : 'IMAGE'}
                  </span>
                </div>

                <div style={styles.assetInfo}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <p style={styles.assetName} title={asset.key}>
                      {asset.altText || asset.key.split('/').pop() || 'media-asset'}
                    </p>
                  </div>

                  <div style={styles.assetMeta}>
                    <span>{asset.width && asset.height ? `${asset.width} × ${asset.height}` : isVid ? 'HD Video' : 'Optimized'}</span>
                    <span>·</span>
                    <span>{formatFileSize(asset.sizeBytes)}</span>
                  </div>

                  <div style={styles.cardActions}>
                    <button
                      onClick={() => copyUrl(asset.id, asset.url)}
                      style={styles.copyBtn}
                      title="Copy Cloudinary CDN URL"
                    >
                      {copiedId === asset.id ? <Check size={13} color="#4ADE80" /> : <Copy size={13} />}
                      <span>{copiedId === asset.id ? 'Copied' : 'Copy CDN URL'}</span>
                    </button>
                    <button
                      onClick={() => setPreviewAsset(asset)}
                      style={styles.iconBtn}
                      title="Preview"
                    >
                      <ExternalLink size={14} />
                    </button>
                    <button
                      onClick={() => handleDelete(asset)}
                      style={{ ...styles.iconBtn, color: '#EF4444' }}
                      title="Delete asset"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Fullscreen Preview Modal ── */}
      {previewAsset && (
        <div style={styles.modalOverlay} onClick={() => setPreviewAsset(null)}>
          <div style={styles.previewModal} onClick={e => e.stopPropagation()}>
            <div style={styles.previewHeader}>
              <div>
                <h3 style={styles.previewTitle}>
                  {previewAsset.resourceType === 'video' ? 'Video Player' : 'Image Preview'}
                </h3>
                <span style={styles.previewUrl}>{previewAsset.url}</span>
              </div>
              <button onClick={() => setPreviewAsset(null)} style={styles.modalCloseBtn}>
                <X size={18} />
              </button>
            </div>

            <div style={styles.previewBody}>
              {previewAsset.resourceType === 'video' || previewAsset.mimeType?.startsWith('video/') ? (
                <video
                  src={previewAsset.url}
                  controls
                  autoPlay
                  style={styles.fullVideo}
                />
              ) : (
                <img
                  src={previewAsset.url}
                  alt={previewAsset.altText || ''}
                  style={styles.fullImg}
                />
              )}
            </div>

            <div style={styles.previewFooter}>
              <div style={{ display: 'flex', gap: '1rem', color: '#9B9B9B', fontSize: '0.8125rem' }}>
                <span>Type: <strong style={{ color: '#FAF7F2' }}>{previewAsset.resourceType || 'image'}</strong></span>
                <span>Size: <strong style={{ color: '#FAF7F2' }}>{formatFileSize(previewAsset.sizeBytes)}</strong></span>
                {previewAsset.width && previewAsset.height && (
                  <span>Dimensions: <strong style={{ color: '#FAF7F2' }}>{previewAsset.width} × {previewAsset.height}</strong></span>
                )}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={() => copyUrl(previewAsset.id, previewAsset.url)}
                  style={styles.btnPrimary}
                >
                  <Copy size={14} />
                  <span>Copy Cloudinary URL</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Add Asset by URL Modal ── */}
      {showUrlModal && (
        <div style={styles.modalOverlay} onClick={() => setShowUrlModal(false)}>
          <div style={styles.dialogModal} onClick={e => e.stopPropagation()}>
            <div style={styles.previewHeader}>
              <h3 style={styles.previewTitle}>Register Cloudinary / External Media URL</h3>
              <button onClick={() => setShowUrlModal(false)} style={styles.modalCloseBtn}>
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleAddByUrl} style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem' }}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Direct Image or Video URL *</label>
                <input
                  type="url"
                  required
                  placeholder="https://res.cloudinary.com/... or https://..."
                  value={inputUrl}
                  onChange={e => setInputUrl(e.target.value)}
                  style={styles.input}
                  autoFocus
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Alt Text / Description (Optional)</label>
                <input
                  type="text"
                  placeholder="e.g. Soba Cardigan drape movement video"
                  value={inputAlt}
                  onChange={e => setInputAlt(e.target.value)}
                  style={styles.input}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '0.5rem' }}>
                <button type="button" onClick={() => setShowUrlModal(false)} style={styles.btnSecondary}>
                  Cancel
                </button>
                <button type="submit" style={styles.btnPrimary} disabled={savingUrl}>
                  {savingUrl && <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />}
                  <span>Add to Media Library</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Cloudinary Setup & Settings Modal ── */}
      {showSetupModal && (
        <div style={styles.modalOverlay} onClick={() => setShowSetupModal(false)}>
          <div style={{ ...styles.guideModal, maxWidth: 540 }} onClick={e => e.stopPropagation()}>
            <div style={styles.previewHeader}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Settings size={18} color="#C9A84C" />
                <h3 style={styles.previewTitle}>Cloudinary & Edge Media Configuration</h3>
              </div>
              <button onClick={() => setShowSetupModal(false)} style={styles.modalCloseBtn}>
                <X size={18} />
              </button>
            </div>

            {/* Storage Status Box */}
            <div style={{
              marginTop: '1rem',
              background: cldConfig?.isConfigured ? 'rgba(74, 222, 128, 0.08)' : 'rgba(201, 168, 76, 0.08)',
              border: cldConfig?.isConfigured ? '1px solid rgba(74, 222, 128, 0.3)' : '1px solid rgba(201, 168, 76, 0.3)',
              borderRadius: 6,
              padding: '10px 14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontSize: '0.8125rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {cldConfig?.isConfigured ? <ShieldCheck size={16} color="#4ADE80" /> : <Sparkles size={16} color="#C9A84C" />}
                <span style={{ color: '#FAF7F2' }}>
                  {cldConfig?.isConfigured
                    ? `Connected to Cloudinary account: ${cldConfig.cloudName}`
                    : 'Cloudflare D1 Native Media Fallback is Active & Operational'}
                </span>
              </div>
              <span style={{
                fontSize: '0.7rem',
                fontWeight: 600,
                color: cldConfig?.isConfigured ? '#4ADE80' : '#C9A84C',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                {cldConfig?.isConfigured ? 'Custom CDN' : 'Edge D1'}
              </span>
            </div>

            {configSuccess && (
              <div style={{ marginTop: '0.75rem', padding: '8px 12px', background: 'rgba(74, 222, 128, 0.15)', border: '1px solid #4ADE80', borderRadius: 6, color: '#4ADE80', fontSize: '0.8125rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                <CheckCircle2 size={14} /> Cloudinary settings saved and verified successfully!
              </div>
            )}

            {/* Interactive Form */}
            <form onSubmit={handleSaveCloudinaryConfig} style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem', marginTop: '1rem' }}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Cloudinary Cloud Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. zarinwear"
                  value={cfgCloudName}
                  onChange={e => setCfgCloudName(e.target.value)}
                  style={styles.input}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>API Key</label>
                  <input
                    type="text"
                    placeholder="e.g. 123456789012345"
                    value={cfgApiKey}
                    onChange={e => setCfgApiKey(e.target.value)}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Upload Preset (Optional)</label>
                  <input
                    type="text"
                    placeholder="e.g. zarin_preset"
                    value={cfgUploadPreset}
                    onChange={e => setCfgUploadPreset(e.target.value)}
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  API Secret {cldConfig?.hasSecret ? '(Secret currently stored securely on edge)' : ''}
                </label>
                <input
                  type="password"
                  placeholder={cldConfig?.hasSecret ? '•••••••••••••••• (Leave blank to keep existing)' : 'Enter API secret...'}
                  value={cfgApiSecret}
                  onChange={e => setCfgApiSecret(e.target.value)}
                  style={styles.input}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '0.5rem' }}>
                <button type="button" onClick={() => setShowSetupModal(false)} style={styles.btnSecondary}>
                  Close
                </button>
                <button type="submit" style={styles.btnPrimary} disabled={savingConfig || !cfgCloudName.trim()}>
                  {savingConfig && <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />}
                  <span>Save & Activate Settings</span>
                </button>
              </div>
            </form>

            <div style={{ marginTop: '1rem', borderTop: '1px solid #28282C', paddingTop: '0.75rem', fontSize: '0.75rem', color: '#888', lineHeight: 1.5 }}>
              Tip: If you do not have Cloudinary API credentials, all uploads seamlessly utilize Cloudflare D1 Native Media with automated image compression, zero timeouts, and global edge caching.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Styles ──────────────────────────────────────────────────
const styles: Record<string, React.CSSProperties> = {
  page: { display: 'flex', flexDirection: 'column', gap: '1.25rem', width: '100%', maxWidth: '100%' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' },
  title: { fontSize: '1.75rem', fontWeight: 600, color: '#FAF7F2', margin: 0 },
  subtitle: { fontSize: '0.875rem', color: '#9B9B9B', margin: '0.25rem 0 0 0' },
  cldBadge: {
    background: 'rgba(201, 168, 76, 0.15)',
    border: '1px solid #C9A84C',
    color: '#C9A84C',
    fontSize: '0.7rem',
    fontWeight: 600,
    padding: '0.15rem 0.5rem',
    borderRadius: '4px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.35rem',
    textTransform: 'uppercase',
  },
  statusBar: {
    background: '#161616',
    border: '1px solid #282828',
    borderRadius: '8px',
    padding: '0.75rem 1rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '0.75rem',
  },
  filterTab: {
    background: 'transparent',
    border: '1px solid #333',
    color: '#888',
    padding: '0.35rem 0.75rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.35rem',
    cursor: 'pointer',
  },
  filterTabActive: {
    background: '#2A2A2A',
    borderColor: '#C9A84C',
    color: '#FAF7F2',
    fontWeight: 600,
  },
  uploadZone: {
    border: '2px dashed #333',
    borderRadius: '10px',
    padding: '2.5rem 1.5rem',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.65rem',
    transition: 'all 0.2s ease',
  },
  uploadIconWrap: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    background: 'rgba(201, 168, 76, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  uploadTitle: { color: '#FAF7F2', fontSize: '1.125rem', margin: 0, fontWeight: 600 },
  uploadDesc: { color: '#888', fontSize: '0.8125rem', maxWidth: '520px', margin: 0, lineHeight: 1.5 },
  btnPrimary: {
    background: '#C9A84C',
    color: '#1A1A1A',
    border: 'none',
    padding: '0.55rem 1.15rem',
    borderRadius: '6px',
    fontWeight: 600,
    fontSize: '0.875rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    cursor: 'pointer',
  },
  btnSecondary: {
    background: 'transparent',
    color: '#FAF7F2',
    border: '1px solid #3A3A3A',
    padding: '0.55rem 1.15rem',
    borderRadius: '6px',
    fontSize: '0.875rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    cursor: 'pointer',
  },
  errorBanner: {
    background: 'rgba(239, 68, 68, 0.15)',
    border: '1px solid #EF4444',
    color: '#FCA5A5',
    padding: '0.75rem 1rem',
    borderRadius: '6px',
    fontSize: '0.8125rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  loadingBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4rem',
    gap: '0.75rem',
  },
  emptyCard: {
    background: '#161616',
    border: '1px dashed #333',
    borderRadius: '8px',
    padding: '4rem 2rem',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.5rem',
  },
  emptyTitle: { fontSize: '1.125rem', color: '#FAF7F2', margin: 0 },
  emptyDesc: { fontSize: '0.8125rem', color: '#888', maxWidth: '420px', margin: '0 0 1rem 0' },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: '1rem',
  },
  assetCard: {
    background: '#1A1A1A',
    border: '1px solid #282828',
    borderRadius: '8px',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    transition: 'border-color 0.15s ease',
  },
  assetMediaWrap: {
    position: 'relative',
    height: '200px',
    background: '#121212',
    cursor: 'pointer',
    overflow: 'hidden',
  },
  assetImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  videoWrap: {
    width: '100%',
    height: '100%',
    position: 'relative',
    background: '#0F0F12',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  assetVideo: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  playOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'rgba(0,0,0,0.35)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  videoBadge: {
    position: 'absolute',
    bottom: 8,
    left: 8,
    background: 'rgba(0,0,0,0.7)',
    color: '#C9A84C',
    fontSize: '0.65rem',
    fontWeight: 700,
    padding: '2px 6px',
    borderRadius: 3,
    letterSpacing: '0.05em',
  },
  formatBadge: {
    position: 'absolute',
    top: 8,
    right: 8,
    background: 'rgba(0,0,0,0.7)',
    color: '#FFF',
    fontSize: '0.65rem',
    fontWeight: 600,
    padding: '2px 6px',
    borderRadius: 3,
  },
  assetInfo: {
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  assetName: {
    color: '#FAF7F2',
    fontWeight: 500,
    fontSize: '0.85rem',
    margin: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  assetMeta: {
    display: 'flex',
    gap: '0.4rem',
    color: '#777',
    fontSize: '0.75rem',
  },
  cardActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    marginTop: '0.25rem',
  },
  copyBtn: {
    flex: 1,
    background: '#242424',
    border: '1px solid #333',
    color: '#FAF7F2',
    padding: '0.4rem 0.6rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.35rem',
    cursor: 'pointer',
  },
  iconBtn: {
    background: '#242424',
    border: '1px solid #333',
    color: '#9B9B9B',
    padding: '0.4rem',
    borderRadius: '4px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.75)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '1rem',
  },
  previewModal: {
    background: '#1A1A1A',
    border: '1px solid #333',
    borderRadius: '10px',
    width: '95vw',
    maxWidth: '840px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  previewHeader: {
    padding: '1rem 1.25rem',
    borderBottom: '1px solid #282828',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  previewTitle: { margin: 0, color: '#FAF7F2', fontSize: '1.125rem', fontWeight: 600 },
  previewUrl: { color: '#777', fontSize: '0.75rem', fontFamily: 'monospace', maxWidth: '600px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', marginTop: 4 },
  modalCloseBtn: { background: 'transparent', border: 'none', color: '#888', cursor: 'pointer' },
  previewBody: {
    flex: 1,
    background: '#0D0D0E',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem',
    minHeight: '360px',
  },
  fullImg: { maxWidth: '100%', maxHeight: '60vh', objectFit: 'contain', borderRadius: 4 },
  fullVideo: { maxWidth: '100%', maxHeight: '60vh', borderRadius: 4, outline: 'none' },
  previewFooter: {
    padding: '1rem 1.25rem',
    borderTop: '1px solid #282828',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  dialogModal: {
    background: '#1A1A1A',
    border: '1px solid #333',
    borderRadius: '8px',
    padding: '1.5rem',
    width: '100%',
    maxWidth: '480px',
  },
  guideModal: {
    background: '#1A1A1A',
    border: '1px solid #333',
    borderRadius: '8px',
    padding: '1.75rem',
    width: '100%',
    maxWidth: '600px',
    maxHeight: '85vh',
    overflowY: 'auto',
  },
  guideStepBox: {
    background: '#141414',
    border: '1px solid #282828',
    borderRadius: '6px',
    padding: '0.85rem 1rem',
  },
  codeBlock: {
    background: '#0B0B0C',
    padding: '0.6rem 0.8rem',
    borderRadius: 4,
    color: '#C9A84C',
    fontSize: '0.78rem',
    margin: '0.5rem 0 0 0',
    overflowX: 'auto',
  },
  formGroup: { display: 'flex', flexDirection: 'column', gap: '0.35rem' },
  label: { fontSize: '0.78rem', color: '#AAA', fontWeight: 500 },
  input: {
    background: '#121212',
    border: '1px solid #333',
    borderRadius: '4px',
    padding: '0.65rem 0.75rem',
    color: '#FAF7F2',
    fontSize: '0.875rem',
    outline: 'none',
  },
};
